import { service } from '@ember/service';
import { Ability } from 'ember-can';
import type AbilitiesService from 'ember-can/services/abilities';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureConfigService from 'garaje/services/feature-config';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type StateService from 'garaje/services/state';
import _intersection from 'lodash/intersection';

export default class ResourceAbility extends Ability {
  @service declare authz: AuthzService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare state: StateService;
  @service declare abilities: AbilitiesService;
  @service declare featureConfig: FeatureConfigService;

  get canUseGeneric(): boolean {
    const hasPermission =
      this.abilities.can('edit reservations') ||
      this.abilities.can('visit desks') ||
      this.abilities.can('visit roomba') ||
      this.abilities.can('edit maps');
    const featureFlagEnabled = this.featureFlags.isEnabled('web-parking-resource');
    const canAccessGenericResources = !!this.state.features?.canAccessGenericResources;

    return featureFlagEnabled && canAccessGenericResources && hasPermission;
  }

  get canVisit(): boolean {
    return this.canUseGeneric;
  }

  get canUseParking(): boolean {
    return this.canVisit && !!this.state.features?.canAccessParking;
  }

  get canUsePartialDayParking(): boolean {
    const partialDayEnabled = this.featureConfig.isEnabled('partialDayParking');
    return this.canUseParking && partialDayEnabled;
  }

  // Enables parking in draft maps
  get canUseDraft(): boolean {
    return this.canUseParking && this.featureFlags.isEnabled('parking-in-drafts');
  }

  get canImport(): boolean {
    return this.canUseParking && this.abilities.can('import-desks desks');
  }

  get canExport(): boolean {
    return this.canUseParking && this.abilities.can('export-desks desks');
  }
}
